import { create } from "zustand";
import { getWorkspaces, createWorkspace, updateWorkspace, deleteWorkspace, type Workspace } from "@/lib/workspaces";
import { removeScreenshot } from "@/lib/workspace-screenshots";

interface WorkspacesState {
  workspaces: Workspace[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  add: (data: { name: string; n8nUrl: string }) => Promise<Workspace>;
  update: (id: string, data: Partial<Pick<Workspace, "name" | "n8nUrl" | "proxyUrl">>) => Promise<Workspace>;
  remove: (id: string) => Promise<void>;
}

export const useWorkspacesStore = create<WorkspacesState>((set, get) => ({
  workspaces: [],
  loading: true,
  error: null,

  async reload() {
    set({ loading: true, error: null });
    try {
      const workspaces = await getWorkspaces();
      set({ workspaces, loading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to load workspaces",
        loading: false,
      });
    }
  },

  async add(data) {
    const workspace = await createWorkspace(data);
    set({ workspaces: [workspace, ...get().workspaces] });
    return workspace;
  },

  async update(id, data) {
    const updated = await updateWorkspace(id, data);
    set({
      workspaces: get().workspaces.map((w) => (w.id === id ? { ...w, ...updated } : w)),
    });
    return updated;
  },

  async remove(id) {
    await deleteWorkspace(id);
    removeScreenshot(id);
    set({ workspaces: get().workspaces.filter((w) => w.id !== id) });
  },
}));
